import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, Search, Send, Paperclip, Loader2, User, Clock, 
  CheckCircle, XCircle, RefreshCw, Trash2, ChevronLeft, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../services/api';

const LiveChatManagement = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');

  useEffect(() => {
    fetchConversations();
    // Poll for new messages every 5 seconds
    pollIntervalRef.current = setInterval(fetchConversations, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/chat/admin/conversations');
      setConversations(response.data || []);
      
      // Update selected chat messages if one is selected
      if (selectedChat) {
        const updatedChat = response.data.find(c => c.id === selectedChat.id);
        if (updatedChat) {
          setMessages(updatedChat.messages || []);
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectChat = async (chat) => {
    setSelectedChat(chat);
    setMessages(chat.messages || []);
    
    // Mark as read
    try {
      await api.post(`/chat/conversations/${chat.id}/mark-read?reader_type=admin`);
      fetchConversations();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat || sending) return;
    
    setSending(true);
    try {
      const response = await api.post(
        `/chat/admin/conversations/${selectedChat.id}/reply?admin_id=${adminInfo.id || 'admin'}&admin_name=${adminInfo.name || 'Support Team'}`,
        { content: newMessage }
      );
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChat) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('chat_id', selectedChat.id);
      
      const uploadResponse = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const response = await api.post(
        `/chat/admin/conversations/${selectedChat.id}/reply?admin_id=${adminInfo.id || 'admin'}&admin_name=${adminInfo.name || 'Support Team'}`,
        {
          content: '',
          attachment_url: uploadResponse.data.url,
          attachment_name: uploadResponse.data.filename,
          attachment_type: uploadResponse.data.content_type
        }
      );
      
      setMessages(prev => [...prev, response.data]);
      fetchConversations();
      toast.success('File sent successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateStatus = async (chatId, status) => {
    try {
      await api.put(`/chat/admin/conversations/${chatId}/status?status=${status}`);
      toast.success(`Chat marked as ${status}`);
      fetchConversations();
      if (selectedChat?.id === chatId) {
        setSelectedChat(prev => ({ ...prev, status }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const deleteChat = async (chatId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      await api.delete(`/chat/admin/conversations/${chatId}`);
      toast.success('Conversation deleted');
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
      fetchConversations();
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-500/20 text-green-400 border-green-500/30',
      closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      resolved: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return styles[status] || styles.open;
  };

  const filteredConversations = conversations.filter(chat => {
    const matchesSearch = chat.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          chat.user_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || chat.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const renderAttachment = (message) => {
    if (!message.attachment_url) return null;
    
    const isImage = message.attachment_type?.startsWith('image/');
    
    if (isImage) {
      return (
        <img 
          src={message.attachment_url} 
          alt={message.attachment_name || 'Attachment'}
          className="max-w-full rounded-lg mt-2 max-h-48 object-contain cursor-pointer"
          onClick={() => window.open(message.attachment_url, '_blank')}
        />
      );
    }
    
    return (
      <a 
        href={message.attachment_url}
        download={message.attachment_name}
        className="flex items-center gap-2 mt-2 p-2 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
      >
        <Paperclip className="h-4 w-4" />
        <span className="text-sm truncate">{message.attachment_name || 'Download file'}</span>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-140px)] flex bg-gray-950 rounded-xl overflow-hidden border border-gray-800">
      {/* Conversations List */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-gray-800 flex flex-col bg-gray-900 ${selectedChat ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-400" />
              Live Chats
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchConversations}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-gray-800 border-gray-700 text-white"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex gap-2">
            {['all', 'open', 'resolved', 'closed'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1 text-xs rounded-full capitalize transition-colors ${
                  statusFilter === status
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <MessageSquare className="h-8 w-8 mb-2" />
              <p className="text-sm">No conversations found</p>
            </div>
          ) : (
            filteredConversations.map((chat) => (
              <button
                key={chat.id}
                onClick={() => selectChat(chat)}
                className={`w-full p-4 border-b border-gray-800 text-left hover:bg-gray-800/50 transition-colors ${
                  selectedChat?.id === chat.id ? 'bg-gray-800' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-bold">
                      {chat.user_name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white truncate max-w-[140px]">
                        {chat.user_name || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 truncate max-w-[140px]">
                        {chat.user_email}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs text-gray-500">
                      {formatTime(chat.updated_at)}
                    </span>
                    {chat.unread_admin > 0 && (
                      <span className="w-5 h-5 bg-purple-600 rounded-full text-xs text-white flex items-center justify-center">
                        {chat.unread_admin}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-400 truncate max-w-[180px]">
                    {chat.last_message || 'No messages'}
                  </p>
                  <Badge className={`text-[10px] ${getStatusBadge(chat.status)}`}>
                    {chat.status}
                  </Badge>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat View */}
      <div className={`flex-1 flex flex-col bg-gray-950 ${!selectedChat ? 'hidden md:flex' : 'flex'}`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-800 bg-gray-900 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedChat(null)}
                  className="md:hidden text-gray-400 hover:text-white"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
                  {selectedChat.user_name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedChat.user_name}</p>
                  <p className="text-xs text-gray-500">{selectedChat.user_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => updateStatus(selectedChat.id, 'resolved')}
                  className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
                >
                  <CheckCircle className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => updateStatus(selectedChat.id, 'closed')}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteChat(selectedChat.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <MessageSquare className="h-12 w-12 mb-3" />
                  <p>No messages in this conversation</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                        message.sender_type === 'admin'
                          ? 'bg-purple-600 text-white rounded-br-md'
                          : 'bg-gray-800 text-gray-100 rounded-bl-md'
                      }`}
                    >
                      {message.sender_type === 'user' && (
                        <p className="text-xs text-purple-400 mb-1 font-medium">{message.sender_name}</p>
                      )}
                      {message.content && (
                        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                      )}
                      {renderAttachment(message)}
                      <p className={`text-xs mt-1 ${
                        message.sender_type === 'admin' ? 'text-purple-200' : 'text-gray-500'
                      }`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-gray-800 bg-gray-900">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-gray-400 hover:text-purple-400"
                >
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your reply..."
                  className="flex-1 bg-gray-800 border-gray-700 text-white"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <MessageSquare className="h-16 w-16 mb-4" />
            <p className="text-lg font-medium">Select a conversation</p>
            <p className="text-sm">Choose a chat from the list to start responding</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveChatManagement;
